import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNDTCategories } from '../../../redux/ProductionData/actions';
import type { UploadResponse } from '../../ndt/types/stats';
import type { NdtCategoryData } from '../../ndt/components/FilterPanel';
import {
  fetchCombinedNdtInsights,
  fetchCombinedNdtSummary,
} from '../utils/fetchCombinedNdt';
import {
  toNdtSummaryPayload,
  type CombinedNdtSelection,
} from '../utils/ndtSummaryPayload';

type Status = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

function normalizeSummary(body: unknown): UploadResponse | null {
  if (!body || typeof body !== 'object') return null;
  const record = body as UploadResponse & { data?: UploadResponse };
  if (record.metadata && Array.isArray(record.evaluations)) return record;
  if (record.data?.metadata && Array.isArray(record.data.evaluations)) return record.data;
  return null;
}

function normalizeInsights(body: unknown): unknown {
  if (!body || typeof body !== 'object') return null;
  const record = body as { mlInsights?: unknown; summary?: unknown; data?: unknown };
  if (record.mlInsights) return record.mlInsights;
  if (record.summary) return record;
  if (record.data) return normalizeInsights(record.data);
  return null;
}

export function useCombinedNdtData({
  projectId,
  pipeIds,
  pipeOptionCount = 0,
}: {
  projectId: string | number | null;
  pipeIds: string[];
  pipeOptionCount?: number;
}) {
  const dispatch = useDispatch();
  const categoryData = useSelector(
    (state: { productionData?: { ndtCategoryData?: NdtCategoryData | null } }) =>
      state.productionData?.ndtCategoryData ?? null,
  );
  const [selection, setSelection] = useState<CombinedNdtSelection>({});
  const [summary, setSummary] = useState<UploadResponse | null>(null);
  const [insights, setInsights] = useState<unknown>(null);
  const [summaryStatus, setSummaryStatus] = useState<Status>('idle');
  const [insightsStatus, setInsightsStatus] = useState<Status>('idle');
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (projectId == null) return;
    dispatch(
      fetchNDTCategories({
        projectId: Number(projectId),
        columnNames: ['wps_number', 'wall_thickness', 'location'],
      }),
    );
  }, [dispatch, projectId]);

  const pipeKey = pipeIds.join('|');
  const selectionKey = JSON.stringify(selection);

  useEffect(() => {
    if (projectId == null) {
      setSummary(null);
      setInsights(null);
      setSummaryStatus('idle');
      setInsightsStatus('idle');
      setSummaryError(null);
      setInsightsError(null);
      return;
    }

    let cancelled = false;
    const allPipes = pipeIds.length === 0 || (pipeOptionCount > 0 && pipeIds.length >= pipeOptionCount);
    const payload = toNdtSummaryPayload(projectId, pipeIds, selection, { allPipes });
    setSummaryStatus('loading');
    setInsightsStatus('loading');
    setSummaryError(null);
    setInsightsError(null);

    fetchCombinedNdtSummary(payload).then((result) => {
      if (cancelled) return;
      const display = normalizeSummary(result.data);
      setSummary(result.noRecords ? null : display);
      setSummaryError(result.error);
      if (result.error) setSummaryStatus('error');
      else if (result.noRecords || !display) setSummaryStatus('empty');
      else setSummaryStatus('ready');
    });

    fetchCombinedNdtInsights(payload).then((result) => {
      if (cancelled) return;
      setInsights(result.noRecords ? null : normalizeInsights(result.data));
      setInsightsError(result.error);
      if (result.error) setInsightsStatus('error');
      else if (result.noRecords || !normalizeInsights(result.data)) setInsightsStatus('empty');
      else setInsightsStatus('ready');
    });

    return () => {
      cancelled = true;
    };
  }, [projectId, pipeKey, selectionKey, reloadKey, pipeIds, selection, pipeOptionCount]);

  return {
    selection,
    onFilterChange: (next: CombinedNdtSelection) =>
      setSelection({
        wpsNumber: next.wpsNumber ?? null,
        wallThickness: next.wallThickness ?? null,
        location: next.location ?? null,
      }),
    categoryData,
    summary,
    insights,
    summaryStatus,
    insightsStatus,
    summaryError,
    insightsError,
    retry: () => setReloadKey((key) => key + 1),
  };
}
